import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import PayrollTabs from '../../components/PayrollTabs';
import ScrollX from '../../components/ScrollX';

// One-time backfill (2026-09-13 request) - no salary sheet has ever
// carried an email column, so this is the only way to get everyone's
// email into Employee Master in bulk instead of one-by-one. Only ever
// updates an existing profile's email; never creates a profile. See
// app/routes/payroll_routes.py's "Bulk email backfill" section.
const PayrollEmailImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setReport(null);
  };

  const handlePreview = async () => {
    if (!file) {
      showToast('Choose a file first', 'error');
      return;
    }
    setPreviewing(true);
    try {
      const res = await payrollService.previewEmailImport(file);
      if (res.data.status === 'error') {
        showToast(res.data.message, 'error');
        setReport(null);
      } else {
        setReport(res.data);
        showToast(`${res.data.update_count} email(s) ready to import, ${res.data.invalid_count} need attention`, res.data.invalid_count ? 'info' : 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to read file', 'error');
      setReport(null);
    }
    setPreviewing(false);
  };

  const handleCommit = async () => {
    if (!file || !report?.update_count) return;
    if (!window.confirm(`Update email addresses for ${report.update_count} employee(s)?`)) return;
    setCommitting(true);
    try {
      const res = await payrollService.commitEmailImport(file);
      showToast(`Updated ${res.data.updated_count} employee(s)`, 'success');
      navigate('/payroll/employees');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Import failed - re-check the file and try again', 'error');
      setReport(null);
    }
    setCommitting(false);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <PayrollTabs />
        <h2 className="text-lg font-semibold text-white mb-1">Bulk Import Employee Emails</h2>
        <p className="text-sm text-white/70 mb-6">
          Upload a simple .xlsx with two columns: <b>Employee Code</b> and <b>Email</b> (a Name column is
          fine too, used only to show you who's who below). Only updates the email on an existing Employee
          Master profile - never creates a new one, and rows whose code isn't found are called out separately.
          Nothing is saved until you press Import.
        </p>

        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <input type="file" accept=".xlsx,.xlsm" onChange={handleFileChange} className="text-sm" />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handlePreview}
              disabled={!file || previewing}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {previewing ? 'Reading...' : 'Check File'}
            </button>
            {report?.update_count > 0 && (
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {committing ? 'Importing...' : `Import ${report.update_count} Email(s)`}
              </button>
            )}
          </div>
        </div>

        {report && (
          <>
            {report.invalid_count > 0 && (
              <div className="glass-card mb-6">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-red-700">{report.invalid_count} row(s) need attention</h2>
                </div>
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Employee Code</th>
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.invalid.map((r, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-2">{r.employee_code}</td>
                          <td className="px-4 py-2">{r.name}</td>
                          <td className="px-4 py-2 text-red-600">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              </div>
            )}

            {report.update_count > 0 && (
              <div className="glass-card">
                <div className="px-5 py-4 border-b border-gray-200">
                  <h2 className="font-semibold text-green-700">{report.update_count} email(s) ready to import</h2>
                </div>
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Employee Code</th>
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">New Email</th>
                        <th className="px-4 py-2 font-medium">Current Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.will_update.map((r) => (
                        <tr key={r.payroll_employee_id} className="border-b border-gray-50 last:border-0">
                          <td className="px-4 py-2">{r.employee_code}</td>
                          <td className="px-4 py-2">{r.name}</td>
                          <td className="px-4 py-2">{r.email}</td>
                          <td className="px-4 py-2 text-gray-400">{r.current_email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollEmailImportPage;
